import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import './index.css'
import App from './App'
import { I18nProvider } from './i18n'
import { AuthProvider } from './auth'
import { StoreProvider } from './data/store'
import ErrorBoundary from './ui/ErrorBoundary'
import StoreSandboxView from './views/StoreSandboxView'
import { storeSandboxSichtbar } from './lib/staysuiteStore'

/*
 * Der Service Worker. Was er tut und was er bewusst NICHT tut, steht in
 * `public/sw.js` — dort auch der Notaus, falls er sich als Fehler erweist.
 *
 * Nur im gebauten Stand: im Entwicklungsbetrieb liefert Vite die Module einzeln
 * und ersetzt sie im laufenden Browser. Ein Service Worker dazwischen beantwortet
 * Anfragen aus dem Speicher, die Vite gerade ersetzt hat — man debuggt dann
 * Gespenster.
 *
 * `updateViaCache: 'none'`, damit der Browser `sw.js` selbst nie aus dem
 * HTTP-Zwischenspeicher nimmt. Sonst greift der Notaus im Ernstfall bis zu einen
 * Tag später, und genau dann zählt jede Stunde.
 *
 * Ohne `await` und ohne Fehlerbehandlung nach außen: schlägt die Registrierung
 * fehl, läuft die Anwendung wie bisher weiter. Ein Zwischenspeicher, der den
 * Start verhindert, hat mehr kaputtgemacht, als er je einspart.
 */
/*
 * Läuft das hier in der nativen Hülle?
 *
 * Daran hängt mehr als eine Nebensächlichkeit: in der Hülle ist die WebView
 * bildschirmfüllend, der Inhalt liegt also UNTER Statusleiste und Dynamic
 * Island, während im Browser dessen eigene Leiste ihn herunterschiebt. Die
 * Klasse macht das für CSS sichtbar; `src/index.css` hängt die sicheren Ränder
 * daran, und der Service Worker unten hängt an ihrem Gegenteil.
 *
 * NICHT über `location.protocol` prüfen. Naheliegend wäre es — auf iOS liefert
 * Capacitor unter `capacitor://` aus. Auf ANDROID aber unter `https://localhost`,
 * und dort wäre die Prüfung still falsch: keine sicheren Ränder, und der Service
 * Worker registrierte sich in der WebView. Ein Fehler, den man erst nach dem
 * ersten Android-Build sucht. `isNativePlatform()` kennt beide Fälle.
 *
 * Als Klasse und nicht als reines `env(safe-area-inset-top)` im Stylesheet,
 * weil dieser Wert auch im mobilen Safari von null verschieden sein kann — die
 * Web-Fassung ist live und richtig, sie soll sich durch die Hülle nicht ändern.
 */
const nativ = Capacitor.isNativePlatform()
if (nativ) document.documentElement.classList.add('nativ')

/*
 * Schnitt 1 braucht gerade KEIN angemeldetes StaySuite-Konto: K8 verbietet
 * ausdrücklich sogar lesenden Zugriff auf die drei Bestandsmandanten. Deshalb
 * steht die Prüffläche VOR AuthProvider und StoreProvider. Sie kann weder eine
 * Sitzung laden noch versehentlich einen bestehenden Betrieb berühren.
 *
 * VITE_STORE_SANDBOX ist nur für den internen Testspur-Build. Der gewöhnliche
 * Produktionsbuild setzt ihn nie; DEV trägt die seitlich installierten Stufen.
 */
const storeSandbox = storeSandboxSichtbar({
  dev: import.meta.env.DEV,
  sandboxBuild: import.meta.env.VITE_STORE_SANDBOX === '1',
  native: nativ,
  platform: Capacitor.getPlatform(),
})

/*
 * NICHT in der nativen Hülle. Die Dateien liegen dort ohnehin schon auf dem
 * Gerät — der Service Worker hätte nichts zu beschleunigen und nichts
 * aufzubewahren. Er würde nur eine zweite Stelle schaffen, an der Dateien
 * zwischengelagert werden, und bei einer App-Aktualisierung wäre wieder zu
 * klären, welche der beiden gewinnt.
 *
 * Auf Android wäre das besonders heimtückisch: dort liefert Capacitor unter
 * `https://localhost` aus, eine Prüfung auf das Schema hielte die WebView also
 * für einen Browser und registrierte den Service Worker.
 */
if (import.meta.env.PROD && !nativ && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .catch(() => { /* dann eben ohne */ })
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      {storeSandbox ? (
        <StoreSandboxView />
      ) : (
        <I18nProvider>
          <AuthProvider>
            <StoreProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </StoreProvider>
          </AuthProvider>
        </I18nProvider>
      )}
    </ErrorBoundary>
  </StrictMode>,
)
